import { useSearchConfig } from "../hooks/useSearchConfig";
import { useNavigate } from "react-router-dom";
import { DEFAULT_ROUTES } from "@/constants/routes";
import SearchVision360 from "../components/search-vision-360";
import { FilterValues } from "../types";
import { Sparkles, Copy, Check, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ModeToggle } from "./components/ModeToggle";
import { useEffect, useState } from "react";
import { apiHubspotIntegrationMs } from "@/services/api-hubspot-integration-ms";
import { createToast } from "vercel-toast";
import { apiVision360 } from "@/services/api-vision360";
import { OrderAlert } from "@/components/order-alert";

export default function SetupLayout() {
  const { config, saveConfig } = useSearchConfig();
  const navigate = useNavigate();
  const [dealId, setDealId] = useState<string>("");
  const [longLists, setLongLists] = useState<any[]>([]);
  const [dealValidation, setDealValidation] = useState<{
    status: boolean;
    message: string;
  }>();
  const [dealData, setDealData] = useState<{
    id: number;
    properties: { dealname?: string };
  }>();
  const [copiedDealId, setCopiedDealId] = useState<string | null>(null);
  const [copiedLink, setCopiedLink] = useState<string | null>(null);

  useEffect(() => {
    if (HIDE_LONG_LISTS) return;

    const loadLongLists = async () => {
      const response = await apiVision360.get(
        `/api/v1/longlists/${config?.userId}`,
      );
      setLongLists(response.data?.longlists);
    };

    if (config?.userId) {
      loadLongLists();
    }
  }, [config?.userId]);

  const handleNext = (filters: FilterValues) => {
    if (config) {
      saveConfig({
        ...config,
        filters: filters,
      });
    }

    if (!dealData?.id) {
      createToast("É necessário verificar um Deal ID válido para continuar", {
        type: "error",
        timeout: 3000,
      });
      return;
    }

    saveConfig({
      ...config,
      currentStep: "curation",
      dealId: dealData?.id,
      dealName: dealData?.properties?.dealname,
    });

    navigate(DEFAULT_ROUTES.VISION_360_BETA_CURATION);
  };

  async function verifyDealId(dealId: string) {
    return await apiHubspotIntegrationMs
      .get(`/deals/${dealId}`)
      .then(function (resp) {
        setDealData(resp.data);
        createToast("Deal verificado com sucesso!", {
          type: "success",
          timeout: 2000,
        });
        setDealValidation({
          status: true,
          message: "",
        });
        saveConfig({
          ...config,
          dealId: resp.data?.id,
          dealName: resp.data?.properties?.dealname,
        });
      })
      .catch(function () {
        setDealValidation({
          status: false,
          message: "Deal inválido",
        });
      });
  }

  const handleCopyDealId = async (dealId: string) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(dealId);
      }

      setCopiedDealId(dealId);
      createToast("Deal ID copiado!", {
        type: "success",
        timeout: 2000,
      });
      setTimeout(() => setCopiedDealId(null), 2000);
    } catch (err) {
      console.error("Erro ao copiar Deal ID:", err);
      createToast("Erro ao copiar Deal ID", {
        type: "error",
        timeout: 2000,
      });
    }
  };

  const handleCopyLink = async (listId: string) => {
    try {
      const link = `${window.location.origin}${DEFAULT_ROUTES.VISION_360_BETA_LONG_LIST}?l_id=${listId}`;

      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(link);
      }

      setCopiedLink(listId);
      createToast("Link da lista copiado!", {
        type: "success",
        timeout: 2000,
      });
      setTimeout(() => setCopiedLink(null), 2000);
    } catch (err) {
      console.error("Erro ao copiar link:", err);
      createToast("Erro ao copiar link", {
        type: "error",
        timeout: 2000,
      });
    }
  };

  const handleCardClick = (list: any) => {
    const longListUrl = `${window.location.origin}${DEFAULT_ROUTES.VISION_360_BETA_LONG_LIST}?l_id=${list.id}`;
    window.open(longListUrl, "_blank");
  };

  const HIDE_LONG_LISTS = true;

  return (
    <div className="flex flex-col items-center justify-center mt-16">
      <div className="flex flex-col items-center gap-2 mb-8">
        <h1 className="text-4xl font-bold text-gray-600">Vision</h1>
        <h2 className="text-md text-gray-600">
          Encontre o Espaço Corporativo Ideal
        </h2>
        <div className="flex items-center gap-2 mt-4 text-gray-500 text-sm">
          <Sparkles className="h-5 w-5" />
          <span>Powered by AI</span>
        </div>
        {import.meta.env.VITE_VISION_AI_MODE_ENABLED === "true" && (
          <div className="mt-6">
            <ModeToggle
              onAIMode={() => navigate(DEFAULT_ROUTES.VISION_360_AI_MODE)}
            />
          </div>
        )}
      </div>

      <div className="flex gap-2">
        <Input
          className="w-full"
          placeholder="Deal ID"
          name="deal_id"
          type="string"
          value={dealId || ""}
          onChange={(event) => {
            setDealId(event.target.value);
            setDealData(undefined);
            setDealValidation(undefined);
          }}
          error={dealValidation?.message}
        />
        <Button
          className="mt-1 txt-sm py-2 px-4 flex-initial w-32 min-h-10"
          onClick={() => {
            verifyDealId(dealId!);
          }}
          disabled={!dealId}
          type="button"
        >
          Verificar Deal
        </Button>
      </div>

      <div className="mx-auto mt-8 w-full max-w-[930px]">
        <SearchVision360 onSearch={handleNext} dealId={dealId ?? null} />
      </div>

      <div className="w-full max-w-[930px] mb-6 mt-6">
        <OrderAlert variant="vision" />
      </div>

      {!HIDE_LONG_LISTS && (
        <>
          <div className="w-full max-w-[1200px] border-t border-gray-200 my-16"></div>
          <div className="w-full max-w-[1200px] mb-6">
            <h3 className="text-lg font-semibold text-gray-700 mb-6">
              Long Lists Anteriores
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {longLists.map((list, index) => (
                <div
                  key={index}
                  className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm hover:shadow-lg transition-all duration-200 cursor-pointer group hover:border-gray-300"
                  onClick={() => handleCardClick(list)}
                >
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-600">
                        Deal ID:
                      </span>
                      <div className="flex items-center gap-2">
                        <span
                          className="text-sm text-gray-900 font-mono truncate max-w-[120px]"
                          title={list.deal_id}
                        >
                          {list.deal_id}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopyDealId(list.deal_id);
                          }}
                          className="p-1 hover:bg-gray-100 rounded transition-colors"
                          title="Copiar Deal ID"
                        >
                          {copiedDealId === list.deal_id ? (
                            <Check className="h-3 w-3 text-green-600" />
                          ) : (
                            <Copy className="h-3 w-3 text-gray-500" />
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-600">
                        Cliente:
                      </span>
                      <span
                        className="text-sm text-gray-900 truncate max-w-[120px]"
                        title={list.client_name}
                      >
                        {list.client_name}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-600">
                        Link da Lista
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopyLink(list.id);
                        }}
                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                        title="Copiar link da lista"
                      >
                        {copiedLink === list.id ? (
                          <Check className="h-3 w-3 text-green-600" />
                        ) : (
                          <Copy className="h-3 w-3 text-gray-500" />
                        )}
                      </button>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                      <span className="text-xs text-gray-500">
                        Clique para visualizar
                      </span>
                      <ArrowRight className="h-4 w-4 text-gray-400 group-hover:text-gray-600 transition-colors" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {longLists.length === 0 && (
              <div className="text-center text-gray-500 py-12">
                <div className="bg-gray-50 rounded-lg p-8">
                  <Sparkles className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600 font-medium">
                    Nenhuma lista encontrada
                  </p>
                  <p className="text-gray-400 text-sm mt-1">
                    Crie sua primeira lista para começar
                  </p>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
